import { generateScenarioSteps, type StepDescriptor } from "@rmnlng/shared";
import { useMemo, useState } from "react";
import { useT } from "@/lib/i18n";
import { type ScenarioSession, useCompleteStep, useScenarioSession } from "@/lib/scenarios";
import { MaterialCollectedScreen } from "./MaterialCollectedScreen";
import { SessionTopBar } from "./SessionTopBar";
import { StepStrip } from "./StepStrip";
import { ChunkStep } from "./steps/ChunkStep";
import { RuleStep } from "./steps/RuleStep";
import { WordStep } from "./steps/WordStep";

type SessionControllerProps = {
  slug: string;
  onExit: () => void;
  // Called when the user taps "Сдать чекпоинт" on the bridge screen.
  // Phase 6 will wire this to the actual checkpoint flow; for now the
  // page can fallback to navigating back to detail.
  onTakeCheckpoint: () => void;
};

/**
 * Top-level session orchestrator.
 *
 * Flow:
 *   1. Fetch session resume state (`GET /scenarios/:slug/session`).
 *      404 → user hasn't started yet, surface empty.
 *   2. Generate the deterministic step list from the scenario's
 *      content via `generateScenarioSteps`. Same input → same output,
 *      so resuming on a different device produces identical indices.
 *   3. Find the first index NOT in `completedStepIndices`.
 *   4. Render the active step; on completion, POST to the step
 *      endpoint and advance.
 *   5. When all steps are done, transition to the bridge screen.
 *
 * Per doctrine §8.3: chrome amber, content neutral. Top bar + step
 * strip use `--scen-*`; the inner step components use the standard
 * Study/quiz palette so the cards feel familiar.
 */
export function SessionController({ slug, onExit, onTakeCheckpoint }: SessionControllerProps) {
  const t = useT();
  const session = useScenarioSession(slug);
  const completeStep = useCompleteStep(slug);
  // Local "I'm done with the steps" flag — flips true the moment the
  // completion mutation returns, so we don't need a fresh fetch to
  // know we're at the bridge screen. The fetch happens in parallel
  // and confirms the server state.
  const [bridge, setBridge] = useState(false);

  // Deterministic step list. Empty until session loads.
  const steps = useMemo<StepDescriptor[]>(() => {
    if (!session.data) return [];
    return generateScenarioSteps(scenarioToInput(session.data));
  }, [session.data]);

  // First incomplete index — that's where we resume.
  const completedSet = useMemo<Set<number>>(() => {
    return new Set(session.data?.completedStepIndices ?? []);
  }, [session.data]);
  const activeIndex = useMemo(() => {
    for (let i = 0; i < steps.length; i++) {
      if (!completedSet.has(i)) return i;
    }
    return -1;
  }, [steps, completedSet]);

  if (session.isLoading) {
    return (
      <div className="flex min-h-[calc(100dvh-80px)] items-center justify-center text-sm text-text-3">
        {t("study.loading")}
      </div>
    );
  }
  if (session.isError || !session.data) {
    return (
      <div className="flex min-h-[calc(100dvh-80px)] flex-col items-center justify-center gap-2 text-center">
        <p className="text-sm text-[color:var(--again)]">
          {session.error instanceof Error ? session.error.message : t("common.failed_to_load")}
        </p>
        <button type="button" onClick={onExit} className="text-[13px] text-text-3 hover:text-text">
          {t("skills.locked_back")}
        </button>
      </div>
    );
  }

  // The server already advanced status to READY_FOR_CHECKPOINT —
  // jump straight to the bridge.
  const showBridge = bridge || session.data.status === "READY_FOR_CHECKPOINT" || activeIndex === -1;

  if (showBridge) {
    return <MaterialCollectedScreen scenario={session.data} onTakeCheckpoint={onTakeCheckpoint} onLater={onExit} />;
  }

  const step = steps[activeIndex]!;

  const advance = (payload?: Record<string, unknown>) => {
    const isLast = activeIndex === steps.length - 1;
    completeStep.mutate(
      {
        stepIndex: activeIndex,
        stepKind: step.kind,
        totalSteps: steps.length,
        payload,
      },
      {
        onSuccess: () => {
          if (isLast) setBridge(true);
        },
      },
    );
  };

  return (
    <div className="flex min-h-[calc(100dvh-40px)] flex-col gap-3.5">
      <SessionTopBar
        scenarioTitle={session.data.title}
        currentStep={activeIndex + 1}
        totalSteps={steps.length}
        onClose={onExit}
      />
      <StepStrip steps={steps} currentIndex={activeIndex} completedIndices={completedSet} />

      {step.kind === "WORD" && (
        <WordStep lemmas={step.lemmas} words={session.data.words} onComplete={() => advance({ lemmas: step.lemmas })} />
      )}
      {step.kind === "RULE" && (
        <RuleStep
          rule={
            session.data.rules.find((r) => r.slug === step.ruleSlug) ?? {
              slug: step.ruleSlug,
              title: null,
              summary: null,
              body: null,
              examples: [],
            }
          }
          onComplete={() => advance({ ruleSlug: step.ruleSlug })}
        />
      )}
      {step.kind === "CHUNK" && (
        <ChunkStep
          scenarioSlug={slug}
          construction={
            session.data.constructions.find((c) => c.id === step.constructionId) ?? {
              id: step.constructionId,
              ro: "",
              ru: "",
              position: 0,
            }
          }
          onComplete={() => advance({ chunkId: step.constructionId })}
        />
      )}
    </div>
  );
}

function scenarioToInput(s: ScenarioSession) {
  return {
    wordLemmas: s.words.map((w) => w.lemma),
    ruleSlugs: s.rules.map((r) => r.slug),
    constructionIds: s.constructions.map((c) => c.id),
  };
}
